import React from 'react'
import { ApolloClient, ApolloLink, split, from } from '@apollo/client'
import { InMemoryCache } from '@apollo/client/cache'
import { onError } from '@apollo/client/link/error'
import { getMainDefinition } from '@apollo/client/utilities'
import { T } from '@transifex/react'
import { createUploadLink } from 'apollo-upload-client'
import { OperationDefinitionNode } from 'graphql'

import { baseUrl } from 'src/api/baseUrl'
// Import the reactive-variable modules directly (not the `src/reactive-variables`
// barrel, which re-exports `actions.ts` -> reducers/routing/utils.ts ->
// `src/utils/impersonator` -> this file) to avoid a circular import.
import {
  INITIAL_ROUTING_DATA,
  routingVar,
} from 'src/reactive-variables/routing'
import { userVar } from 'src/reactive-variables/user'
import { message } from 'src/ui/toast'
import {
  endImpersonatingBeforeLogout,
  impersonatorTokenKey,
} from 'src/utils/impersonator'

import introspectionToPossibleTypes from './cache'
import introspectionQueryResultData from './fragmentTypes.json'
import { haulerGuardDeniedMessages } from './haulerGuardDeniedMessages'
import { classifySessionError } from './session-error'
import { wsLink } from './ws'
import history from '../../history'

const httpLink = createUploadLink({
  uri: `${baseUrl}/graph-api/`,
}) as unknown

export const getJWTToken = () => {
  return localStorage.getItem('reactQLJWT')
}
export const getImpersonatorToken = () => {
  return localStorage.getItem(impersonatorTokenKey) || getJWTToken()
}

const authMiddleware = new ApolloLink((operation, forward) => {
  const useImpersonatorTokenIfExist =
    operation.getContext()?.useImpersonatorTokenIfExist
  const token = useImpersonatorTokenIfExist
    ? getImpersonatorToken()
    : getJWTToken()
  operation.setContext({
    headers: {
      authorization: token ? `JWT ${token}` : '',
    },
  })

  return forward(operation)
})

const logoutLink = onError(({ networkError, graphQLErrors }) => {
  // PRO-141: the unauthenticated-call message is only a logout when there is
  // genuinely no session — with a live token it is a permission (403-class)
  // denial that must surface to the caller and NEVER end the session. The
  // classification lives in `session-error.ts` (pure, unit-tested).
  const kind = classifySessionError({
    networkStatusCode:
      networkError && (networkError as $TSFixMe).statusCode
        ? (networkError as $TSFixMe).statusCode
        : undefined,
    graphQLErrorMessages: (graphQLErrors ?? []).map((error) => error.message),
    hasSessionToken: !!getJWTToken(),
    sessionVerified: routingVar().init,
  })

  // One-shot guard for both error shapes: once the app has already reacted
  // to a 401 and landed on /login, further in-flight requests failing with
  // the same stale token shouldn't re-run the cleanup/redirect.
  if (kind === 'logout' && history.location.pathname !== '/login') {
    // FORGE-231: every other logout path (top-nav "Log out", boot-time
    // checkTokenRefreshTokenOrLogout, ...) clears the dead token and resets
    // routingVar/userVar before navigating. This one didn't, so routingVar
    // stayed 'authenticated' - the /login route's `and: ['non-authenticated']`
    // guard immediately redirected straight back to the dashboard, and every
    // still-mounted pollInterval query kept re-triggering the same 401,
    // producing a repeated /login <-> /app/dashboard flip (the "shaking").
    window.localStorage.removeItem('reactQLJWT')
    endImpersonatingBeforeLogout()
    routingVar({
      ...INITIAL_ROUTING_DATA,
      init: true,
      roles: ['non-authenticated'],
    })
    userVar(null)
    history.push('/login')
  }
})

// Contractor (hauler) same-hauler write guards raise these exact messages
// (fleet_management/services/hauler_guard.py). They are NOT `errorMsg401`, so logoutLink
// ignores them — surface them as a toast instead of failing silently. Deliberately narrow
// (only these messages) so it can't double-toast or alter any other error handling.
// The list itself lives in ./haulerGuardDeniedMessages so forms can import it
// without this module's client setup; re-exported here for existing callers.
export { haulerGuardDeniedMessages }

const haulerGuardDeniedLink = onError(({ graphQLErrors }) => {
  graphQLErrors?.forEach((error) => {
    if (haulerGuardDeniedMessages.includes(error.message)) {
      message.error(error.message)
    }
  })
})

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const requestFailedLink = onError(
  ({ networkError, graphQLErrors, operation }) => {
    const isQuery =
      (
        operation.query.definitions.find(
          (definition) => definition.kind === 'OperationDefinition'
        ) as OperationDefinitionNode
      )?.operation === 'query'

    // showNetworkError would be False by default for mutations and True for queries since in most mutations we already show a message
    const { showNetworkError = isQuery, showGraphqlError = isQuery } =
      operation.getContext()

    if (showNetworkError && networkError?.message) {
      message.error(
        networkError.message || (
          <T _str="Something went wrong (Network error)" />
        )
      )
    }
    if (showGraphqlError && graphQLErrors?.length) {
      graphQLErrors.forEach((error) => {
        message.error(
          <T
            _str="Something went wrong (GraphQL error: { path } query)"
            path={error.path?.join(', ')}
          />
        )
      })
    }
  }
)

const cache = new InMemoryCache({
  possibleTypes: introspectionToPossibleTypes(introspectionQueryResultData),
})

const link = split(
  // split based on operation type
  ({ query }) => {
    const definition = getMainDefinition(query)
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    )
  },
  wsLink,
  httpLink as ApolloLink
)

const apolloClient = new ApolloClient({
  // TODO: add requestFailedLink when it would be necessary
  //
  // FE-PERF-2 FE2.4 transport note: do NOT add BatchHttpLink here.
  // The backend refuses array payloads outright ("The received data
  // is not a valid JSON query.", verified against the graph-api
  // 2026-07-19), so batching would break every query; the deployed
  // API negotiates HTTP/2 (verified: curl reports http/2 against
  // dev-platform-api), so parallel POSTs multiplex on one connection
  // without it. Query dedupe is Apollo's default (queryDeduplication:
  // true) and is relied on by the module's paired readers.
  link: from([logoutLink, haulerGuardDeniedLink, authMiddleware, link]),
  cache,
  defaultOptions: {
    watchQuery: {
      // PRO-131: default watch queries load cache-and-network, then demote to
      // cache-first on later same-variable renders/remounts so back-navigation
      // feels instant. Variable changes (pagination/search/filter/date) revert
      // to the initial policy, so those reobservations revalidate over the
      // network. Queries with an explicit fetchPolicy (network-only / no-cache
      // / etc.) pass through unchanged, so their policy is preserved.
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy(currentFetchPolicy, context) {
        if (context.reason === 'variables-changed') {
          return context.initialFetchPolicy
        }
        if (currentFetchPolicy === 'cache-and-network') {
          return 'cache-first'
        }
        return currentFetchPolicy
      },
    },
  },
})

// PRO-443: bound Apollo cache growth over the all-day operator sessions this app
// targets. The InMemoryCache has no typePolicies/keyArgs, so long sessions
// accumulate normalized entities that become unreachable as list queries
// refetch under new filter/page/search variables. A periodic gc() reclaims
// them. `cache.gc()` only ever collects UNREACHABLE objects, so it can never
// affect a live query's result — safe to run unconditionally. (Adding `keyArgs`
// field policies on the high-cardinality list roots is the complementary fix;
// it is deferred because each needs per-query runtime validation of its
// pagination/merge behaviour.)
const CACHE_GC_INTERVAL_MS = 5 * 60 * 1000
if (typeof window !== 'undefined') {
  window.setInterval(() => {
    apolloClient.cache.gc()
  }, CACHE_GC_INTERVAL_MS)
}

export default apolloClient
